import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import {
  providerErrorReason,
  trackConnectorConnectFailed,
} from '@/lib/analytics/connectionTracking'
import {
  type GoogleCalendarAuthState,
  GOOGLE_CALENDAR_AUTH_STATE_STORAGE_KEY,
  GOOGLE_CALENDAR_REDIRECT_PATH,
} from '@/lib/auth/google-calendar'
import { safeParseRnd } from '@/lib/auth/oauth-state'

// Google Calendar OAuth callback. Google redirects here with `?code=…&state=…`; we validate
// state and hand the code to the backend, which keeps the refresh token encrypted.
export function AuthGoogleCalendarPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateAsync: connectGoogleCalendar } =
    trpc.brandConnection.connectGoogleCalendar.useMutation()

  const isRunning = useRef(false)
  const code = searchParams.get('code')
  const state = searchParams.get('state')
  const error = searchParams.get('error')

  useEffect(() => {
    if (isRunning.current) return
    isRunning.current = true

    let storedState: GoogleCalendarAuthState | null = null
    try {
      storedState = JSON.parse(
        localStorage.getItem(GOOGLE_CALENDAR_AUTH_STATE_STORAGE_KEY) || 'null',
      ) as GoogleCalendarAuthState | null
    } catch {
      storedState = null
    }
    const referrerPath = storedState?.referrerPath ?? '/settings/connections'

    // Required, not checked-only-when-present: a crafted callback link with no state would
    // otherwise attach the attacker's calendar to whichever brand the victim is signed into.
    if (!state || !storedState || storedState.rnd !== safeParseRnd(state)) {
      // An `?error=…` without state is the user cancelling or Google failing, not a forged
      // callback. Nothing gets connected on this path either way.
      const reason = !state && error ? providerErrorReason(error) : 'state_mismatch'
      trackConnectorConnectFailed('google_calendar', reason, error ?? undefined)
      if (reason === 'cancelled') toast('Google Calendar connection cancelled')
      else
        toast.error('Google Calendar connection failed', {
          description: 'Security check did not match.',
        })
      localStorage.removeItem(GOOGLE_CALENDAR_AUTH_STATE_STORAGE_KEY)
      isRunning.current = false
      navigate(referrerPath)
      return
    }

    if (error || !code) {
      const reason = error ? providerErrorReason(error) : 'missing_code'
      trackConnectorConnectFailed('google_calendar', reason, error ?? undefined)
      // Closing the consent screen is a choice, not a failure worth a red toast.
      if (reason === 'cancelled') toast('Google Calendar connection cancelled')
      else toast.error('Google Calendar connection failed', { description: error ?? undefined })
      localStorage.removeItem(GOOGLE_CALENDAR_AUTH_STATE_STORAGE_KEY)
      isRunning.current = false
      navigate(referrerPath)
      return
    }

    connectGoogleCalendar({
      code,
      redirectUri: `${window.location.origin}${GOOGLE_CALENDAR_REDIRECT_PATH}`,
    })
      .then((res) => {
        toast.success('Google Calendar connected', { description: res.accountEmail ?? undefined })
        navigate(referrerPath)
      })
      .catch((e: unknown) => {
        trackConnectorConnectFailed('google_calendar', 'authorize_failed', e)
        toast.error('Failed to connect Google Calendar', {
          description: e instanceof Error ? e.message : undefined,
        })
        navigate(referrerPath)
      })
      .finally(() => {
        localStorage.removeItem(GOOGLE_CALENDAR_AUTH_STATE_STORAGE_KEY)
        isRunning.current = false
      })
  }, [code, state, error, connectGoogleCalendar, navigate])

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Connecting Google Calendar...</p>
      </div>
    </div>
  )
}
